import { useEffect, useMemo, useState } from "react";
import Autocomplete from "react-autocomplete";
import { Exercise } from "../../../Types/Courses";
import Button from "../../../UI/Button/Button";

interface ExerciseLinkerProps {
  exercises: Exercise[];
  selectedExerciseId: number | "";
  loading: boolean;
  canLoadMore: boolean;
  onSearchChange: (value: string) => void;
  onSelectExercise: (exerciseId: number | "") => void;
  onOpenCreateModal: () => void;
  onLoadMore: () => void;
  onGoToDetails: () => void;
}

export default function ExerciseLinker(props: ExerciseLinkerProps) {
  const {
    exercises,
    selectedExerciseId,
    loading,
    canLoadMore,
    onSearchChange,
    onSelectExercise,
    onOpenCreateModal,
    onLoadMore,
    onGoToDetails,
  } = props;

  const [inputValue, setInputValue] = useState("");

  const selectedExercise = useMemo(
    () => exercises.find((item) => item.id === selectedExerciseId) || null,
    [exercises, selectedExerciseId]
  );

  useEffect(() => {
    if (selectedExercise) {
      setInputValue(selectedExercise.title);
      return;
    }
    if (!selectedExerciseId) {
      setInputValue("");
    }
  }, [selectedExercise, selectedExerciseId]);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium text-gray-800">Exercice lié</label>
        <div className="flex gap-2">
          <Button color="primary" onClick={onOpenCreateModal}>
            + Créer un exercice
          </Button>
          {selectedExerciseId ? (
            <Button color="info" onClick={onGoToDetails}>
              Détails
            </Button>
          ) : null}
        </div>
      </div>

      <div>
        <p className="text-xs text-gray-600 mb-1">Sélectionne via autocomplétion</p>
        <Autocomplete
          getItemValue={(item) => item.title}
          items={exercises}
          value={inputValue}
          onChange={(event) => {
            const nextValue = event.target.value;
            setInputValue(nextValue);
            onSearchChange(nextValue);
            if (!nextValue.trim()) {
              onSelectExercise("");
            }
          }}
          onSelect={(value, item: Exercise) => {
            setInputValue(value);
            onSelectExercise(item.id);
          }}
          renderItem={(item, isHighlighted) => (
            <div
              key={item.id}
              className={`px-2 py-2 cursor-pointer text-sm ${
                isHighlighted ? "bg-amber-100" : "bg-white"
              }`}
            >
              {item.title}
            </div>
          )}
          renderMenu={(children) => (
            <div className="border border-gray-200 rounded bg-white shadow-lg mt-1 max-h-56 overflow-y-auto z-20 relative">
              {children}
            </div>
          )}
          inputProps={{
            className: "form-input w-full",
            placeholder: "Rechercher un exercice...",
          }}
          wrapperStyle={{
            display: "block",
            width: "100%",
          }}
        />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-600">
          Sélectionne un exercice existant ou crée-en un, puis sauvegarde la leçon.
        </p>
        {canLoadMore ? (
          <Button disabled={loading} onClick={onLoadMore}>
            {loading ? "Chargement..." : "Charger plus"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
